import React from "react";
import { motion } from "framer-motion";

export default function ProjectsSection() {
  // Honest capability examples. `desc` is a one-line summary and `features`
  // are genuine things each product does — NO invented clients or metrics.
  const projects = [
    {
      icon: "📡",
      title: "Website & Database Monitoring Tool",
      category: "SaaS / Monitoring",
      desc: "Round-the-clock monitoring for your websites and databases, so you hear about problems before your users do.",
      features: [
        "Instant email alert the moment a site or database goes down",
        "SSL expiry warnings sent 30, 15 & 3 days in advance",
        "Tracks API & database response times to catch slowdowns",
        "Works with MySQL, Redis & MongoDB",
      ],
      tech: ["Next.js", "TypeScript", "Node.js", "MySQL", "Redis", "MongoDB"],
      color: "#00ff9d",
    },
    {
      icon: "✅",
      title: "Task & Project Management Platform",
      category: "Web App",
      desc: "One workspace that replaces scattered chat apps, spreadsheets, and separate video-call tools.",
      features: [
        "Task boards, assignments, deadlines & progress tracking",
        "Built-in audio, video & group calling",
        "Real-time sync so the whole team stays in the loop",
      ],
      tech: ["React", "Node.js", "Socket.io", "WebRTC", "MongoDB", "Redis"],
      color: "#00d4ff",
    },
    {
      icon: "🏗️",
      title: "Cloud Management Dashboard",
      category: "Cloud / DevOps",
      desc: "A simple GUI to run your entire AWS setup—without ever opening the raw AWS console.",
      features: [
        "Create & resize servers in a few clicks",
        "Manage S3 storage, security groups & auto-scaling",
        "Role-based access so you can safely delegate",
      ],
      tech: ["AWS", "Terraform", "Node.js", "Next.js", "MongoDB", "Redis"],
      color: "#ff6b35",
    },
    {
      icon: "🎓",
      title: "AI Virtual Classroom",
      category: "AI Platform",
      desc: "An interactive e-learning platform that keeps learners engaged instead of passively watching videos.",
      features: [
        "Pick a character voice to hear every lesson in",
        "Quizzes after each lesson with score tracking",
        "AI helper answers questions in the same character's voice",
      ],
      tech: ["Node.js", "Next.js", "OpenAI", "ElevenLabs", "MongoDB", "Redis"],
      color: "#667eea",
    },
    {
      icon: "🤖",
      title: "AI-Powered Business Automation",
      category: "AI / Automation",
      desc: "Smart chatbots and GPT-powered automations that take repetitive work off your team's plate.",
      features: [
        "Handles common customer queries 24/7",
        "Automates manual data entry & routine tasks",
        "Hands off to a human only when it's really needed",
      ],
      tech: ["Python", "OpenAI", "Node.js", "REST APIs"],
      color: "#fa709a",
    },
  ];

  return (
    <section className="projects" id="projects">
      <h2>What We Build</h2>
      <p className="section-subtitle">
        Real-world problems and the kind of solutions we build to solve them
      </p>

      <div className="projects-grid">
        {projects.map((project, i) => (
          <motion.div
            key={project.title}
            className="project-card"
            style={{ "--card-color": project.color }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            whileHover={{ y: -10, boxShadow: `0 28px 60px ${project.color}33` }}
          >
            {/* Glow header */}
            <div
              className="project-glow-header"
              style={{
                background: `linear-gradient(135deg, ${project.color}26, ${project.color}08)`,
                borderBottom: `1px solid ${project.color}33`,
              }}
            >
              <span
                className="project-category"
                style={{
                  color: project.color,
                  background: `${project.color}1f`,
                  borderColor: `${project.color}4d`,
                }}
              >
                {project.category}
              </span>
              <div
                className="project-icon"
                style={{
                  background: `linear-gradient(135deg, ${project.color}, ${project.color}99)`,
                  boxShadow: `0 10px 28px ${project.color}66`,
                }}
              >
                {project.icon}
              </div>
            </div>

            {/* Body */}
            <div className="project-body">
              <h3>{project.title}</h3>
              <p className="project-desc">{project.desc}</p>

              <ul className="project-features">
                {project.features.map((f, idx) => (
                  <li key={idx}>
                    <span
                      className="project-check"
                      style={{ color: project.color }}
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="project-tech">
                <div className="tech-label">Built with</div>
                <div className="tech-tags">
                  {project.tech.map((t, idx) => (
                    <span key={idx} className="tech-tag">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="projects-cta"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
      >
        <p>Have a project in mind? Let's build it together.</p>
        <a href="#contact" className="projects-cta-button">
          Discuss Your Project
        </a>
      </motion.div>
    </section>
  );
}
